import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaBell,
  FaBookOpen,
  FaCalendarAlt,
  FaChalkboardTeacher,
  FaChartLine,
  FaCheck,
  FaCheckCircle,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaChevronUp,
  FaClipboardCheck,
  FaClipboardList,
  FaDatabase,
  FaFileInvoiceDollar,
  FaGlobe,
  FaLock,
  FaMobileAlt,
  FaMoneyCheckAlt,
  FaQuestionCircle,
  FaQuoteLeft,
  FaSchool,
  FaServer,
  FaShieldAlt,
  FaTimes,
  FaUserCircle,
  FaUserGraduate,
  FaUsers,
  FaUserShield,
  FaVideo,
} from "react-icons/fa";
import { getPublicFeedback, getPublicSchools } from "../api/publicContent";
import { recordPublicVisitor } from "../api/visitorAnalytics";
import {
  getDashboardPathForRole,
  MANAGEMENT_ROLES,
  normalizeRole,
  STUDENT_ROLES,
} from "../utils/authRole";
import { useUsageTracker } from "../context/UsageTrackerContext";
import Seo from "../components/Seo";
import schoolOperationsImage from "../assets/home-slider/school-operations.webp";
import studentGrowthImage from "../assets/home-slider/student-growth.webp";
import parentConnectionImage from "../assets/home-slider/parent-connection.webp";
import classnxHomeHero from "../assets/classnx-home-hero-v2.png";

const heroSlides = [
  {
    eyebrow: "One connected school workspace",
    title: "Run every school day with clarity.",
    description:
      "Bring admissions, academics, fees, and daily operations into one calm, connected experience.",
    image: schoolOperationsImage,
    alt: "School administrators using ClassNX together in a modern school office",
  },
  {
    eyebrow: "Every learner, clearly understood",
    title: "Turn progress into potential.",
    description:
      "Give teachers the insight and time they need to support every student with confidence.",
    image: studentGrowthImage,
    alt: "Teacher helping students review their learning progress in class",
  },
  {
    eyebrow: "Families always in the loop",
    title: "Bring school and home closer.",
    description:
      "Share updates, milestones, and important moments through communication parents can trust.",
    image: parentConnectionImage,
    alt: "Parents checking a school update while their daughter studies at home",
  },
];

const ProfessionalHero = () => (
  <section className="relative isolate overflow-hidden bg-gradient-to-b from-slate-50 via-blue-50/30 to-white text-slate-900 border-b border-slate-200/80">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(219,234,254,0.45),transparent_65%)] pointer-events-none" />
    <div className="relative mx-auto max-w-7xl px-5 pt-14 pb-16 sm:px-6 sm:pt-16 sm:pb-20 lg:pt-20 lg:pb-24">
      <div className="grid items-center gap-12 lg:grid-cols-[0.94fr_1.06fr] lg:gap-16">
        <div className="public-hero-copy">


          <h1 className="mt-6 max-w-2xl text-4xl font-black leading-[1.06] tracking-[-0.04em] text-slate-900 sm:text-5xl lg:text-6xl xl:text-[4.25rem]">
            One School.<br />
            <span className="text-blue-700">
              One Clear View.
            </span>
          </h1>

          <p className="mt-6 max-w-xl text-base leading-7 text-slate-600 sm:text-lg sm:leading-8">
            ClassNX is the operating system for modern schools, uniting admissions, academics, attendance, smart fees, parent communication, and analytics into one dependable, multi-tenant cloud workspace. Built with isolated school subdomains, zero data leakage, and lightning-fast workflows.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/schools/request-demo"
              className="group inline-flex items-center justify-center gap-3 rounded-xl bg-blue-600 px-7 py-4 font-bold text-white shadow-lg shadow-blue-600/25 transition duration-200 hover:-translate-y-0.5 hover:bg-blue-700"
            >
              Book a Personalised Demo <FaArrowRight className="text-sm transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-7 py-4 font-bold text-slate-800 shadow-sm transition duration-200 hover:border-slate-400 hover:bg-slate-50"
            >
              Sign In to Your School
            </Link>
          </div>

          {/* Quick Direct Class Join Bar */}
          <div className="mt-5 max-w-xl rounded-2xl border border-blue-200/90 bg-gradient-to-r from-blue-50/90 via-sky-50 to-blue-50/60 p-3.5 sm:p-4 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white shadow-md shadow-blue-600/25">
                  <FaVideo className="text-base" />
                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                  </span>
                </span>
                <div>
                  <p className="text-xs font-black uppercase tracking-wider text-blue-900">
                    Live Class / Tuition Entry
                  </p>
                  <p className="text-xs text-slate-600">
                    Have a meeting code? Join your live lecture directly.
                  </p>
                </div>
              </div>
              <Link
                to="/join-class"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-xs font-bold text-white shadow-md shadow-blue-600/20 transition hover:bg-blue-700 hover:-translate-y-0.5 shrink-0"
              >
                <span>Join Class Here</span>
                <FaArrowRight className="text-[10px]" />
              </Link>
            </div>
          </div>

          <div className="mt-10 grid max-w-xl grid-cols-3 gap-4 border-t border-slate-200/80 pt-6 text-sm">
            <div>
              <strong className="block text-slate-900 text-base font-bold">Custom Subdomain</strong>
              <span className="text-slate-500 text-xs sm:text-sm">your-school.classnx.com</span>
            </div>
            <div>
              <strong className="block text-slate-900 text-base font-bold">Zero Leakage</strong>
              <span className="text-slate-500 text-xs sm:text-sm">Strict tenant isolation</span>
            </div>
            <div>
              <strong className="block text-slate-900 text-base font-bold">CBSE / ICSE Ready</strong>
              <span className="text-slate-500 text-xs sm:text-sm">Pre-built compliance</span>
            </div>
          </div>
        </div>

        <div className="relative lg:pl-2">
          <div className="relative overflow-hidden rounded-2xl border border-slate-200/90 bg-white p-2.5 shadow-2xl shadow-slate-200/70">
            <img
              src={classnxHomeHero}
              alt="Teacher and students collaborating with technology in a modern Indian school"
              width="2016"
              loading="eager"
              decoding="async"
              className="h-[22rem] w-full rounded-xl object-cover object-[68%_center] sm:h-[27rem] lg:h-[31rem]"
            />
            <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3.5 rounded-xl border border-slate-200/90 bg-white/95 p-4 shadow-xl backdrop-blur-md">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-blue-50 text-blue-700 shadow-inner">
                <FaChartLine className="text-lg" />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-blue-700">The Operating System for Modern Schools</p>
                <p className="text-sm font-bold text-slate-900">Technology that keeps learning & security at the centre</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const quickActions = [
  {
    label: "Open Dashboard",
    description: "Go to your main dashboard and continue your work.",
    icon: FaChartLine,
    path: "dashboard",
    roles: "all",
  },
  {
    label: "My Profile",
    description: "Update your details and manage your account.",
    icon: FaUserCircle,
    path: "/profile",
    roles: "all",
  },
  {
    label: "Admission Form",
    description: "Fill your admission details quickly.",
    icon: FaClipboardList,
    path: "/admission-form",
    roles: "student",
  },
  {
    label: "View Admission",
    description: "Check the current admission form status.",
    icon: FaBookOpen,
    path: "/view-admission-form",
    roles: "student",
  },
  {
    label: "Admin Board",
    description: "Manage users, records, and school operations.",
    icon: FaUserGraduate,
    path: "/admin-board",
    roles: "admin",
  },
  {
    label: "Notifications",
    description: "Review recent alerts and system updates.",
    icon: FaBell,
    path: "/admin-board#notifications",
    roles: "admin",
  },
];

const platformModules = [
  {
    id: "admissions",
    label: "Admissions & CRM",
    icon: FaUserGraduate,
    badge: "Paperless Enrollment",
    title: "End-to-End Online Admissions & Student Onboarding",
    description:
      "Transform scattered paperwork into an automated enrollment pipeline. Capture inquiries, collect online applications, verify documents, and issue admission letters effortlessly.",
    stats: [
      { label: "Faster Processing", value: "85%" },
      { label: "Paperwork Reduced", value: "100%" },
      { label: "Parent Satisfaction", value: "98%" },
    ],
    features: [
      "Customizable online inquiry & admission application forms",
      "Automated SMS & WhatsApp status updates to parents",
      "Digital document verification and student document vault",
      "Instant student profile & roll number generation upon fee confirmation",
    ],
  },
  {
    id: "fees",
    label: "Smart Fees & UPI",
    icon: FaMoneyCheckAlt,
    badge: "Auto-Reconciliation",
    title: "Zero-Leakage Fee Collection with Instant Digital Receipts",
    description:
      "Eliminate manual fee registers. Accept instant UPI, card, and netbanking payments directly into school accounts with automated GST invoices and automated WhatsApp payment reminders.",
    stats: [
      { label: "Cash Handling Loss", value: "₹0" },
      { label: "Auto-Reconciliation", value: "100%" },
      { label: "Collection Speed", value: "3x" },
    ],
    features: [
      "Flexible fee heads: tuition, transport, lab, uniform, and activity dues",
      "Automated scheduled WhatsApp & SMS alerts to parents before due dates",
      "Instant GST-compliant receipts with verifiable QR codes",
      "Real-time defaulter lists and accountant ledger balance sheets",
    ],
  },
  {
    id: "academics",
    label: "Academics & NEP 2020",
    icon: FaChalkboardTeacher,
    badge: "Smart Timetables",
    title: "Intelligent Timetable Builder & Continuous Assessments",
    description:
      "Empower academic coordinators and teachers. Generate conflict-free teacher schedules, track syllabus coverage, conduct examinations, and print report cards.",
    stats: [
      { label: "Timetable Conflicts", value: "0" },
      { label: "Report Card Gen", value: "1-Click" },
      { label: "NEP 2020 Ready", value: "100%" },
    ],
    features: [
      "AI-assisted automated timetable generation with teacher substitution manager",
      "Pre-configured grading schemes for CBSE, ICSE, and State education boards",
      "Continuous formative and summative assessment gradebook",
      "Customizable digital report cards with teacher remarks and attendance",
    ],
  },
  {
    id: "attendance",
    label: "Attendance & Campus",
    icon: FaCalendarAlt,
    badge: "Real-Time Tracking",
    title: "Live Attendance, RFID Integration & Gate Safety",
    description:
      "Keep campus operations synchronized. Mark class attendance on mobile or sync with biometric/RFID turnstiles. Parents receive instant notification if their child is absent.",
    stats: [
      { label: "Parent Alert Latency", value: "< 5s" },
      { label: "Attendance Accuracy", value: "99.9%" },
      { label: "Safety Verification", value: "Active" },
    ],
    features: [
      "Mobile app attendance for teachers with 1-click bulk submission",
      "Instant SMS/WhatsApp notification to parents for unexcused absences",
      "Staff biometric/fingerprint integration and leave approval workflow",
      "Visitor gate-pass management and digital campus security logs",
    ],
  },
  {
    id: "analytics",
    label: "Executive Analytics",
    icon: FaChartLine,
    badge: "Trust & Leadership",
    title: "Central Command Dashboard for Principals & Trustees",
    description:
      "Provide school leadership with clarity. Monitor student strength, fee collection cash flow, teacher workload distribution, and multi-branch performance in real time.",
    stats: [
      { label: "Executive Visibility", value: "360°" },
      { label: "Decision Latency", value: "Real-Time" },
      { label: "Multi-Branch Support", value: "Native" },
    ],
    features: [
      "Live fee collection velocity vs projected operational budgets",
      "Grade-wise student retention and dropout warning indicators",
      "Teacher workload, class coverage, and subject progress trackers",
      "Exportable statutory audit summaries and board affiliation reports",
    ],
  },
];

const comparisonData = [
  {
    feature: "Dedicated Tenant Subdomain (abc.classnx.com)",
    classnx: "Yes (Dedicated Isolated Portal)",
    legacy: "No (Single shared install or local PC)",
    spreadsheets: "No",
  },
  {
    feature: "Automatic Cross-Domain Login Routing",
    classnx: "Yes (Instant redirection from classnx.com)",
    legacy: "No (Manual URL discovery)",
    spreadsheets: "No",
  },
  {
    feature: "Multi-Tenant Security & Tenant Isolation",
    classnx: "Strict Application-Level Boundary",
    legacy: "Shared or vulnerable local database",
    spreadsheets: "Zero security (files easily copied)",
  },
  {
    feature: "Automated Fee Collection & Instant GST Receipts",
    classnx: "Yes (UPI, QR, Cards with Auto-Reconciliation)",
    legacy: "Manual bank statement uploads",
    spreadsheets: "Manual calculation & Excel formulas",
  },
  {
    feature: "Real-Time Parent Portal & WhatsApp Alerts",
    classnx: "Yes (Live updates for attendance & dues)",
    legacy: "Costly SMS add-ons / delayed",
    spreadsheets: "Manual messaging one by one",
  },
  {
    feature: "Cloud Backups & 99.98% High Availability",
    classnx: "Yes (Continuous automated encrypted backups)",
    legacy: "Manual pen drive / local hard disk backups",
    spreadsheets: "Prone to corruption & file loss",
  },
];

const securityPillars = [
  {
    icon: FaGlobe,
    title: "Subdomain-Isolated Routing",
    description:
      "Every institution operates under its verified subdomain namespace (e.g. abc.classnx.com). Visitors logging in via classnx.com are safely routed to their dedicated tenant portal.",
  },
  {
    icon: FaLock,
    title: "HttpOnly Secure Session Cookies",
    description:
      "Session tokens are transmitted using HttpOnly, Secure, and SameSite=Lax flags scoped to .classnx.com. Scripts cannot read or leak session credentials.",
  },
  {
    icon: FaUserShield,
    title: "Strict Tenant Authorization Gate",
    description:
      "The server-side security filter verifies that the authenticated user's tenant ID matches the subdomain tenant identity on every request before returning records.",
  },
  {
    icon: FaDatabase,
    title: "Encrypted Backups & 99.98% Uptime",
    description:
      "Automated continuous cloud backups with point-in-time recovery ensure your school never loses a student record, attendance log, or fee receipt.",
  },
];

const faqs = [
  {
    q: "How does subdomain login work when users visit classnx.com?",
    a: "When a student, parent, teacher, or administrator visits classnx.com and logs in with their credentials, ClassNX identifies their school tenant and automatically redirects them to their dedicated school subdomain (e.g. https://abc.classnx.com/dashboard) with their active session preserved.",
  },
  {
    q: "Can our school staff and parents bookmark and log in directly from our subdomain?",
    a: "Yes! Every school has its own custom branded entrance (e.g. https://yourschool.classnx.com). Teachers, students, and parents can bookmark and log in directly from your school's subdomain without ever needing to go through the central portal.",
  },
  {
    q: "How does ClassNX ensure zero data leakage between different schools?",
    a: "ClassNX enforces strict multi-tenant isolation at both the routing and data access layer. Every database transaction and API endpoint validates the tenant boundary against the authenticated session token, ensuring one school can never see or touch another school's data.",
  },
  {
    q: "How long does it take to migrate our existing student and fee data?",
    a: "Our school onboarding specialists assist with guided Excel/CSV migration templates for student profiles, staff records, class structures, and opening fee balances. Most schools go live in under 48 hours.",
  },
  {
    q: "Is ClassNX compatible with CBSE, ICSE, and State Board grading systems?",
    a: "Yes. ClassNX includes pre-configured assessment structures, NEP 2020 skill-based evaluation rubrics, and customizable grading scales that conform to CBSE, ICSE, and various state education department requirements.",
  },
];

const schoolJourney = [
  { title: "Admit", text: "Capture inquiries and enroll students without scattered paperwork.", icon: FaClipboardList },
  { title: "Teach", text: "Coordinate classes, timetables, attendance, and assessments.", icon: FaChalkboardTeacher },
  { title: "Collect", text: "Track fees, receipts, pending balances, and reminders clearly.", icon: FaMoneyCheckAlt },
  { title: "Understand", text: "Turn everyday school activity into actionable executive insights.", icon: FaChartLine },
  { title: "Connect", text: "Keep families informed with timely, relevant communication.", icon: FaUsers },
];

const normalizeSchoolItem = (item = {}, fallbackIndex = 0) => ({
  name:
    item.name
    || item.schoolName
    || item.title
    || `School ${fallbackIndex + 1}`,
  location: item.location || item.city || item.address || "Location unavailable",
  board: item.board || item.curriculum || item.affiliation || "Board unavailable",
  focus:
    item.focus
    || item.description
    || item.summary
    || "School profile coming soon.",
});

const normalizeFeedbackItem = (item = {}, fallbackIndex = 0) => ({
  quote:
    item.quote
    || item.message
    || item.feedback
    || item.testimonial
    || "Feedback content coming soon.",
  author:
    item.author
    || item.authorName
    || item.name
    || `User ${fallbackIndex + 1}`,
  role: item.role || item.authorRole || item.designation || "School Team",
  school: item.school || item.schoolName || item.organization || "ClassNX School",
});

const roleHighlights = {
  default: ["Dashboard", "Profile", "Recent updates"],
  student: ["Admission", "Profile", "Study progress"],
  admin: ["Dashboard", "Notifications", "Management tools"],
};

const homeSeoKeywords = [
  "school management software",
  "school management system",
  "school ERP software",
  "student information system",
  "multi tenant school software",
  "subdomain school portal",
  "school administration software",
  "education ERP",
  "admission management system",
  "fee management software",
  "attendance management system",
  "exam management system",
  "parent communication app",
  "timetable management software",
  "school analytics dashboard",
];

const Home = ({ userRole, embedded = false }) => {
  const role = normalizeRole(userRole);
  const isLoggedIn = !!role;
  const dashboardPath = getDashboardPathForRole(role);
  const isStudent = STUDENT_ROLES.includes(role);
  const isAdmin = MANAGEMENT_ROLES.includes(role);

  const { getCurrentSessionTime, getSessionDuration } = useUsageTracker();
  const [sessionDisplay, setSessionDisplay] = useState(() =>
    getSessionDuration(getCurrentSessionTime())
  );
  const [activeFeedbackIndex, setActiveFeedbackIndex] = useState(0);
  const [schools, setSchools] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [activeModuleTab, setActiveModuleTab] = useState(platformModules[0].id);
  const [expandedFaq, setExpandedFaq] = useState(null);

  useEffect(() => {
    if (!isLoggedIn) return;
    const interval = setInterval(() => {
      setSessionDisplay(getSessionDuration(getCurrentSessionTime()));
    }, 1000);
    return () => clearInterval(interval);
  }, [isLoggedIn, getCurrentSessionTime, getSessionDuration]);

  useEffect(() => {
    if (isLoggedIn || feedback.length === 0) return undefined;
    const slideTimer = setInterval(() => {
      setActiveFeedbackIndex((current) => (current + 1) % feedback.length);
    }, 5000);
    return () => clearInterval(slideTimer);
  }, [feedback.length, isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) return undefined;

    let isMounted = true;

    const loadPublicContent = async () => {
      const [schoolResponse, feedbackResponse] = await Promise.all([
        getPublicSchools(),
        getPublicFeedback(),
      ]);

      if (!isMounted) return;

      if (Array.isArray(schoolResponse) && schoolResponse.length > 0) {
        setSchools(schoolResponse.map(normalizeSchoolItem));
      }

      if (Array.isArray(feedbackResponse) && feedbackResponse.length > 0) {
        const normalizedFeedback = feedbackResponse.map(normalizeFeedbackItem);
        setFeedback(normalizedFeedback);
        setActiveFeedbackIndex((current) => current % normalizedFeedback.length);
      }
    };

    const recordVisitorPresence = () => {
      try {
        const promise = recordPublicVisitor();
        if (promise && typeof promise.catch === "function") {
          promise.catch(() => {
            // Visitor analytics must never interrupt public experience.
          });
        }
      } catch {
        // Visitor analytics must never interrupt public experience.
      }
    };

    loadPublicContent();
    recordVisitorPresence();
    const visitorRefreshId = window.setInterval(recordVisitorPresence, 60000);

    return () => {
      isMounted = false;
      window.clearInterval(visitorRefreshId);
    };
  }, [isLoggedIn]);

  if (isLoggedIn) {
    const hour = new Date().getHours();
    const greeting =
      hour >= 5 && hour < 12
        ? "Good morning"
        : hour >= 12 && hour < 17
          ? "Good afternoon"
          : hour >= 17 && hour < 21
            ? "Good evening"
            : "Hello";
    const highlights = isAdmin
      ? roleHighlights.admin
      : isStudent
        ? roleHighlights.student
        : roleHighlights.default;
    const filteredActions = quickActions
      .filter((action) => {
        if (action.roles === "all") return true;
        if (action.roles === "student") return isStudent;
        if (action.roles === "admin") return isAdmin;
        return false;
      })
      .map((action) => ({
        ...action,
        path: action.path === "dashboard" ? dashboardPath : action.path,
      }));

    return (
      <div className={embedded ? "bg-slate-50 text-slate-900" : "min-h-screen bg-slate-50 text-slate-900"}>
        <section className={`relative overflow-hidden bg-gradient-to-br from-blue-700 via-blue-600 to-teal-600 text-white ${embedded ? "px-4 py-8 md:px-6 md:py-10" : "px-6 py-12"}`}>
          <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full border-[26px] border-white/10" />
          <div className="absolute -bottom-24 right-24 h-48 w-48 rounded-full border-[18px] border-teal-200/10" />
          <div className="relative mx-auto max-w-6xl">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-blue-100">
              {greeting}
            </p>
            <h1 className="mt-3 max-w-3xl text-3xl font-black tracking-tight md:text-5xl">
              Welcome back to your ClassNX workspace
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-blue-100 md:text-base">
              Open the tools you need for today and keep your school operations moving smoothly without digging through menus.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              {highlights.map((item) => (
                <span
                  key={item}
                  className="rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-bold backdrop-blur-sm"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className={`mx-auto max-w-6xl ${embedded ? "-mt-4 px-4 pb-8 md:px-6" : "-mt-8 px-6 pb-14"}`}>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredActions.map((action) => {
              const Icon = action.icon;
              return (
                <Link
                  key={action.label}
                  to={action.path}
                  className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-lg"
                >
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                    <Icon className="text-lg" />
                  </div>
                  <h2 className="mt-4 text-lg font-black text-slate-900 transition-colors group-hover:text-blue-700">
                    {action.label}
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-slate-500">
                    {action.description}
                  </p>
                  <div className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-blue-700">
                    Open now <FaArrowRight className="text-xs transition group-hover:translate-x-1" />
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="mt-8 grid gap-5 lg:grid-cols-[1.35fr_0.65fr]">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-7">
              <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
                    Workspace status
                  </p>
                  <h2 className="mt-2 text-2xl font-black text-slate-900">
                    Ready when you are
                  </h2>
                </div>
                <span className="inline-flex w-fit rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700">
                  Account active
                </span>
              </div>
              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                    Session
                  </p>
                  <p className="mt-2 text-lg font-black text-slate-900">Active</p>
                  <p className="mt-1 text-sm text-slate-500">Current workspace session is running.</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                    Session time
                  </p>
                  <p className="mt-2 text-lg font-black text-slate-900">{sessionDisplay}</p>
                  <p className="mt-1 text-sm text-slate-500">Measured while this account is active.</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                    Security
                  </p>
                  <p className="mt-2 text-lg font-black text-slate-900">Protected</p>
                  <p className="mt-1 text-sm text-slate-500">Manage credentials from your profile.</p>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-7">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
                Helpful route
              </p>
              <h2 className="mt-2 text-2xl font-black text-slate-900">Account controls</h2>
              <p className="mt-3 text-sm leading-6 text-slate-500">
                Review identity, school subscription, and password controls from your profile.
              </p>
              <Link
                to="/profile"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-blue-700"
              >
                Open Profile <FaArrowRight className="text-xs" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const currentTab = platformModules.find((tab) => tab.id === activeModuleTab) || platformModules[0];
  const CurrentTabIcon = currentTab.icon;

  return (
    <div className="public-home min-h-screen bg-slate-50 text-slate-900">
      <Seo
        title="ClassNX - School Management Software and Multi-Tenant School ERP"
        description="ClassNX is an enterprise school management system offering dedicated school subdomains, zero-leakage fee management, admissions CRM, exams, LMS, and parent communication."
        canonical="/"
        keywords={homeSeoKeywords}
      />

      <ProfessionalHero />

      {/* Trust & Proof Bar */}
      <section className="border-y border-slate-200 bg-white py-8">
        <div className="mx-auto max-w-7xl px-5 sm:px-6">
          <div className="grid grid-cols-2 gap-6 md:grid-cols-4 md:gap-8">
            <div className="text-center">
              <p className="text-2xl font-black text-blue-900 sm:text-3xl">99.98%</p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-500">High Availability SLA</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-black text-blue-900 sm:text-3xl">100%</p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Tenant Data Isolation</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-black text-blue-900 sm:text-3xl">Instant</p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Subdomain Auto-Routing</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-black text-blue-900 sm:text-3xl">CBSE / ICSE</p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Curriculum Compliance</p>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Platform Modules Tabs */}
      <section id="features" className="bg-slate-50 px-5 py-16 sm:px-6 md:py-24">
        <div className="mx-auto max-w-7xl">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-blue-600">The Operating System for Modern Schools</p>
            <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-900 sm:text-4xl md:text-5xl">
              Engineered for every department in your institution.
            </h2>
            <p className="mt-4 text-base leading-7 text-slate-600 sm:text-lg">
              Say goodbye to juggling four different software vendors. ClassNX provides deeply integrated modules tailored for administrators, teachers, accountants, and parents.
            </p>
          </div>

          {/* Module Tab Switcher */}
          <div className="mt-12 flex flex-wrap justify-center gap-2 sm:gap-3">
            {platformModules.map((tab) => {
              const TabIcon = tab.icon;
              const isActive = tab.id === activeModuleTab;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveModuleTab(tab.id)}
                  className={`inline-flex items-center gap-2.5 rounded-full px-5 py-3 text-sm font-bold transition-all duration-200 ${isActive
                      ? "bg-blue-600 text-white shadow-md shadow-blue-600/25"
                      : "bg-white text-slate-700 border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50"
                    }`}
                >
                  <TabIcon className={isActive ? "text-white" : "text-blue-600"} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Tab Showcase Card */}
          <div className="mt-8 overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/50 md:p-10">
            <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_0.9fr]">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-blue-700">
                  <CurrentTabIcon className="text-xs" /> {currentTab.badge}
                </span>
                <h3 className="mt-4 text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">
                  {currentTab.title}
                </h3>
                <p className="mt-4 text-base leading-7 text-slate-600">
                  {currentTab.description}
                </p>

                <div className="mt-8 grid gap-3 sm:grid-cols-2">
                  {currentTab.features.map((feature) => (
                    <div key={feature} className="flex items-start gap-2.5 text-sm text-slate-700">
                      <FaCheckCircle className="mt-1 text-emerald-600 shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <Link
                    to="/schools/request-demo"
                    className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-blue-600/25 transition hover:bg-blue-700"
                  >
                    Schedule Walkthrough <FaArrowRight className="text-xs" />
                  </Link>
                  <Link
                    to="/services"
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-slate-50 px-6 py-3.5 text-sm font-bold text-slate-700 transition hover:bg-slate-100"
                  >
                    View All Services
                  </Link>
                </div>
              </div>

              {/* Stats & Preview Card */}
              <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-800 p-6 text-white shadow-xl sm:p-8">
                <div className="flex items-center justify-between border-b border-white/10 pb-5">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-blue-300">Module Performance</p>
                    <p className="text-lg font-bold text-white mt-1">{currentTab.label}</p>
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-500/20 text-blue-300 text-xl">
                    <CurrentTabIcon />
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-3 gap-3">
                  {currentTab.stats.map((stat) => (
                    <div key={stat.label} className="rounded-xl border border-white/10 bg-white/5 p-3 text-center backdrop-blur-sm">
                      <p className="text-xl font-black text-white sm:text-2xl">{stat.value}</p>
                      <p className="mt-1 text-[11px] font-semibold text-slate-300 leading-tight">{stat.label}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-6 rounded-xl border border-white/10 bg-slate-950/60 p-4 text-xs leading-relaxed text-slate-300">
                  <p className="font-semibold text-white mb-1"><FaCheckCircle className="inline text-emerald-400 mr-1.5" /> Automated Subdomain Routing</p>
                  When your school logs in from any domain, all activity in this module is securely restricted to your unique tenant database.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* School Journey Section */}
      <section className="bg-gradient-to-b from-white to-slate-50 px-5 py-16 text-slate-900 sm:px-6 md:py-24">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-6 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-blue-600">One continuous journey</p>
              <h2 className="mt-3 text-3xl font-black tracking-tight sm:text-4xl md:text-5xl">
                Every school workflow, finally connected.
              </h2>
            </div>
            <p className="max-w-2xl text-lg leading-8 text-slate-600">
              Replace scattered registers, spreadsheets, and message threads with a shared system that keeps every team working from the same accurate information.
            </p>
          </div>

          <div className="relative mt-12 grid gap-4 md:grid-cols-5">
            <div className="absolute left-[10%] right-[10%] top-8 hidden h-px bg-gradient-to-r from-blue-200 via-blue-400 to-blue-200 md:block" aria-hidden="true" />
            {schoolJourney.map((step, index) => {
              const Icon = step.icon;
              return (
                <article
                  key={step.title}
                  className="group relative border border-slate-200 bg-white p-5 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-blue-300 hover:shadow-lg rounded-2xl"
                >
                  <div className="relative z-10 flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50 text-xl text-blue-700 shadow-sm border border-blue-100">
                    <Icon />
                  </div>
                  <p className="mt-6 text-xs font-black uppercase tracking-[0.18em] text-blue-600">0{index + 1}</p>
                  <h3 className="mt-2 text-xl font-black">{step.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-500">{step.text}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Side-by-Side Comparison Matrix */}
      <section className="bg-white px-5 py-16 sm:px-6 md:py-24 border-t border-slate-200">
        <div className="mx-auto max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-blue-600">Objective Comparison</p>
            <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-900 sm:text-4xl md:text-5xl">
              Why leading schools are upgrading to ClassNX.
            </h2>
            <p className="mt-4 text-base leading-7 text-slate-600 sm:text-lg">
              Compare our dedicated multi-tenant architecture with legacy desktop ERPs and error-prone spreadsheets.
            </p>
          </div>

          <div className="overflow-x-auto rounded-3xl border border-slate-200 shadow-xl">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-900 text-white">
                  <th className="py-5 px-6 font-bold text-sm sm:text-base">Operational Capability</th>
                  <th className="py-5 px-6 font-black text-white text-sm sm:text-base bg-blue-700">ClassNX Enterprise</th>
                  <th className="py-5 px-6 font-semibold text-slate-300 text-sm sm:text-base">Legacy Desktop ERPs</th>
                  <th className="py-5 px-6 font-semibold text-slate-300 text-sm sm:text-base">Spreadsheets & WhatsApp</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 text-sm">
                {comparisonData.map((row, idx) => (
                  <tr key={row.feature} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                    <td className="py-4 px-6 font-bold text-slate-800">{row.feature}</td>
                    <td className="py-4 px-6 font-bold text-blue-900 bg-blue-50/50">
                      <div className="flex items-center gap-2">
                        <FaCheck className="text-emerald-600 shrink-0" />
                        <span>{row.classnx}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-slate-600">
                      <div className="flex items-center gap-2">
                        <FaTimes className="text-rose-500 shrink-0" />
                        <span>{row.legacy}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-slate-600">
                      <div className="flex items-center gap-2">
                        <FaTimes className="text-rose-500 shrink-0" />
                        <span>{row.spreadsheets}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Ironclad Security & Multi-Tenancy Architecture Pillar */}
      <section className="relative isolate overflow-hidden bg-[#0a1526] px-5 py-16 text-white sm:px-6 md:py-24 border-y border-slate-800">
        <div className="relative mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-emerald-400">
              <FaShieldAlt /> Enterprise Security & Data Governance
            </div>
            <h2 className="mt-4 text-3xl font-black tracking-tight sm:text-4xl md:text-5xl text-white">
              Engineered with zero compromise on tenant security.
            </h2>
            <p className="mt-4 text-base leading-7 text-slate-300 sm:text-lg">
              School data contains sensitive student records, parent contact details, and financial transactions. Our architecture guarantees strict multi-tenant isolation, safe cross-subdomain authentication, and zero data leakage.
            </p>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {securityPillars.map((pillar) => {
              const PillarIcon = pillar.icon;
              return (
                <div
                  key={pillar.title}
                  className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur transition duration-300 hover:-translate-y-1 hover:border-blue-400/40 hover:bg-white/[0.08]"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-500/20 text-xl text-blue-300">
                    <PillarIcon />
                  </div>
                  <h3 className="mt-5 text-lg font-bold text-white">{pillar.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-300">{pillar.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Schools Directory & Social Proof */}
      <section className="relative isolate overflow-hidden bg-slate-50 px-6 py-16 text-slate-900 md:py-24 border-b border-slate-200">
        <div className="relative mx-auto max-w-7xl">
          <div className="mb-12 max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-blue-600">ClassNX Community</p>
            <h2 className="mt-3 text-4xl font-black tracking-tight md:text-5xl text-slate-900">
              {schools.length > 0 ? "Our Partner Schools" : "See the whole school clearly"}
            </h2>
            <p className="mt-4 text-lg leading-8 text-slate-600">
              {schools.length > 0
                ? "Meet verified school teams building more connected learning communities with ClassNX."
                : "A focused workspace for daily decisions—without invented customer names or inflated claims."}
            </p>
          </div>

          {schools.length > 0 ? (
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {schools.map((school) => (
                <article
                  key={school.name}
                  className="group rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-blue-300 hover:shadow-lg"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                    <FaSchool />
                  </div>
                  <h3 className="mt-5 text-lg font-bold text-slate-900">{school.name}</h3>
                  <p className="mt-2 text-sm font-semibold text-blue-600">{school.location}</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.16em] text-slate-500">{school.board}</p>
                  <p className="mt-4 text-sm leading-6 text-slate-600">{school.focus}</p>
                </article>
              ))}
            </div>
          ) : (
            <div className="grid items-center gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:gap-16">
              <div>
                <h3 className="max-w-2xl text-3xl font-black leading-tight tracking-tight sm:text-4xl text-slate-900">
                  Give every role the right view—without creating more silos.
                </h3>
                <p className="mt-5 max-w-xl text-base leading-7 text-slate-600">
                  Leadership sees the whole operation. Administrators keep records moving. Teachers stay focused on learning. Families receive the updates that matter.
                </p>
                <div className="mt-7 grid gap-3 text-sm font-semibold text-slate-800 sm:grid-cols-2 lg:grid-cols-1">
                  <span className="border-l-2 border-blue-600 pl-3">Role-based workspaces</span>
                  <span className="border-l-2 border-blue-600 pl-3">Connected school records</span>
                  <span className="border-l-2 border-blue-600 pl-3">Clear operational visibility</span>
                </div>
                <Link
                  to="/schools/request-demo"
                  className="group mt-8 inline-flex items-center gap-3 rounded-xl bg-blue-600 px-6 py-3.5 font-bold text-white shadow-lg shadow-blue-600/25 transition duration-200 hover:-translate-y-0.5 hover:bg-blue-700"
                >
                  See it with your school’s workflow <FaArrowRight className="text-sm transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
              <div className="relative">
                <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-2.5 shadow-xl">
                  <img
                    src={require("../assets/classnx-dashboard-hero.png")}
                    alt="ClassNX school management dashboard interface"
                    loading="lazy"
                    width="1200"
                    height="750"
                    className="w-full rounded-xl object-cover"
                  />
                  <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between gap-3 rounded-xl border border-slate-200/90 bg-white/95 p-4 shadow-lg backdrop-blur-md">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-700">Real product view</p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">Built for day-to-day school operations</p>
                    </div>
                    <FaChartLine className="shrink-0 text-xl text-blue-600" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Testimonials / Feedback Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-slate-50 px-6 py-16 text-slate-900 md:py-24">
        <div className="relative mx-auto max-w-6xl">
          <div className="mb-10 max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-blue-600">Voices from the community</p>
            <h2 className="mt-3 text-4xl font-black tracking-tight md:text-5xl">School Feedback</h2>
            <p className="mt-4 text-lg leading-8 text-slate-600">
              Hear directly from educators, administrators, and trustees experiencing the ClassNX difference.
            </p>
          </div>

          {feedback.length > 0 ? (
            <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-xl shadow-slate-200/40 md:p-10">
              <div className="mb-7 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-600 text-lg text-white shadow-md shadow-blue-600/20">
                <FaQuoteLeft />
              </div>
              <p className="text-lg leading-8 text-slate-800 md:text-xl">“{feedback[activeFeedbackIndex].quote}”</p>
              <p className="mt-6 text-base font-bold text-slate-900">{feedback[activeFeedbackIndex].author}</p>
              <p className="text-sm text-slate-500">
                {feedback[activeFeedbackIndex].role} · {feedback[activeFeedbackIndex].school}
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  {feedback.map((slide, index) => (
                    <button
                      key={`${slide.author}-${index}`}
                      type="button"
                      onClick={() => setActiveFeedbackIndex(index)}
                      className={`h-2.5 rounded-full transition-all ${index === activeFeedbackIndex ? "w-8 bg-blue-600" : "w-2.5 bg-slate-300 hover:bg-slate-400"
                        }`}
                      aria-label={`Go to feedback slide ${index + 1}`}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveFeedbackIndex((current) => (current - 1 + feedback.length) % feedback.length)}
                    className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveFeedbackIndex((current) => (current + 1) % feedback.length)}
                    className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl shadow-slate-200/40 lg:grid-cols-[0.85fr_1.15fr]">
              <div className="relative flex min-h-64 items-center justify-center overflow-hidden bg-gradient-to-br from-blue-700 to-indigo-800 p-10">
                <div className="relative flex h-24 w-24 items-center justify-center rounded-2xl border border-white/20 bg-white/10 text-4xl text-white shadow-xl backdrop-blur">
                  <FaQuoteLeft />
                </div>
              </div>
              <div className="flex flex-col justify-center p-8 sm:p-10 lg:p-12">
                <p className="text-xs font-bold uppercase tracking-[0.24em] text-blue-600">Help shape what comes next</p>
                <h3 className="mt-3 text-3xl font-black tracking-tight text-slate-900">Share your ClassNX experience.</h3>
                <p className="mt-4 max-w-xl text-base leading-7 text-slate-600">
                  Tell us what works, what could be better, and what your school needs next. With your permission, verified feedback may be featured here.
                </p>
                <Link
                  to="/feedback"
                  className="group mt-7 inline-flex w-fit items-center gap-3 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-blue-600/25 transition duration-200 hover:-translate-y-0.5 hover:bg-blue-700"
                >
                  Share feedback <FaArrowRight className="text-xs transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          )}
          {feedback.length > 0 && (
            <div className="mt-7 text-center">
              <Link
                to="/feedback"
                className="group inline-flex items-center gap-3 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-blue-600/25 transition hover:-translate-y-0.5 hover:bg-blue-700"
              >
                Share your feedback <FaArrowRight className="text-xs transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Interactive FAQ Accordion */}
      <section className="bg-white px-5 py-16 sm:px-6 md:py-24 border-t border-slate-200">
        <div className="mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-blue-600">Got Questions?</p>
            <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
              Frequently Asked Questions
            </h2>
            <p className="mt-3 text-slate-600">
              Clear answers to common questions about subdomains, security, and onboarding.
            </p>
          </div>

          <div className="divide-y divide-slate-200 rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            {faqs.map((faq, index) => {
              const isExpanded = expandedFaq === index;
              return (
                <div key={faq.q} className="bg-white transition">
                  <button
                    type="button"
                    onClick={() => setExpandedFaq(isExpanded ? null : index)}
                    className="flex w-full items-center justify-between p-6 text-left font-bold text-slate-900 transition hover:bg-slate-50"
                    aria-expanded={isExpanded}
                  >
                    <span className="text-base sm:text-lg pr-4">{faq.q}</span>
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-slate-100 text-slate-600 text-sm">
                      {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </span>
                  </button>
                  {isExpanded && (
                    <div className="px-6 pb-6 text-sm sm:text-base leading-relaxed text-slate-600 bg-slate-50/50">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* High-Converting Bottom CTA Banner */}
      <section className="relative isolate overflow-hidden bg-gradient-to-r from-blue-700 via-blue-800 to-indigo-900 px-5 py-16 text-white sm:px-6 md:py-20 shadow-xl">
        <div className="relative mx-auto flex max-w-6xl flex-col justify-between gap-8 lg:flex-row lg:items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-blue-200">A clearer school day starts here</p>
            <h2 className="mt-3 max-w-3xl text-3xl font-black tracking-tight sm:text-4xl md:text-5xl text-white">
              Ready to bring your school into one connected workspace?
            </h2>
            <p className="mt-4 max-w-2xl text-base font-normal leading-7 text-blue-100">
              Claim your school&apos;s custom subdomain today. We&apos;ll configure your workspace and demonstrate how ClassNX eliminates administrative friction.
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-3">
            <Link
              to="/schools/request-demo"
              className="group inline-flex items-center gap-3 rounded-xl bg-white px-7 py-4 font-bold text-blue-900 shadow-xl transition hover:-translate-y-0.5 hover:bg-blue-50"
            >
              Request a School Demo <FaArrowRight className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/login"
              className="rounded-xl border border-white/30 bg-white/10 px-7 py-4 font-bold text-white backdrop-blur-md transition hover:bg-white/20"
            >
              Sign In
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
